import { Paper, Typography } from '@material-ui/core'
import React from 'react'

import useStyles from './styles/index.styles'

/**
 * One card surface for this screen.
 *
 * `<Paper style={{ padding: 16, marginBottom: 16 }}>` appeared eight times
 * across seven files with an identical inline object. Two costs, and the second
 * is the reason this exists rather than a tidy-up:
 *
 *  - The literals bypass the spacing scale. They happen to equal
 *    `theme.spacing(2)`, so nothing looked wrong, but a scale nothing goes
 *    through is not a scale.
 *  - Every surface asserted the same weight. The conversation and a policy
 *    reference read as equally important, so the page had nothing to scan for
 *    — and demoting the reference column meant editing eight places, which is
 *    why it had not been done.
 *
 * `quiet` is that demotion, available in one edit now. Reference material gets
 * an outline instead of a raised surface; the conversation keeps the only
 * elevation on the page.
 *
 * The heading is a prop rather than a child because its LEVEL is the thing that
 * kept going wrong. One panel omitted `component` entirely and MUI mapped
 * subtitle2 to `h6`, three levels below its siblings; another argued itself
 * into `h2` on the grounds of being a sibling of "Ticket properties" while
 * rendering inside it. Making the level a required prop means the next panel
 * cannot forget it.
 */
export default function SectionCard({
  heading,
  level,
  headingId,
  quiet,
  children
}: {
  heading: string
  /** Required, not defaulted — see the note above on why. */
  level: 'h2' | 'h3'
  /** Set when something inside the card is `aria-labelledby` this heading. */
  headingId?: string
  /** Reference material: outlined rather than raised. */
  quiet?: boolean
  children?: React.ReactNode
}) {
  const classes = useStyles()
  return (
    <Paper
      elevation={quiet ? 0 : 1}
      className={quiet ? classes.panelCardQuiet : classes.panelCard}>
      <Typography variant="subtitle2" component={level} id={headingId}>
        {heading}
      </Typography>
      {children}
    </Paper>
  )
}
